import type { GeoJSONFeature, PaddingOptions } from 'mapbox-gl';
import type { TrailheadInView, TrailheadStatus } from '@alltrails/types/Trailhead';

// The trailheads MVT is only available at zoom 4 and above.
export const trailheadsMinZoom = 4;

type TrailheadMvtProperties = {
  name: string | null;
  status: TrailheadStatus;
  trail_count: number;
};

type RenderedTrailheadFeature = Pick<GeoJSONFeature, 'id' | 'properties'> & Partial<Pick<GeoJSONFeature, 'geometry'>>;

export function trailheadsFromFeatures(features: RenderedTrailheadFeature[]): TrailheadInView[] {
  const trailheads = new Map<string, TrailheadInView>();
  for (const feature of features) {
    if (feature.id === undefined || !feature.properties) {
      continue;
    }
    const { name, status, trail_count: trailCount } = feature.properties as TrailheadMvtProperties;
    const coordinates = feature.geometry?.type === 'Point' ? feature.geometry.coordinates : undefined;
    trailheads.set(String(feature.id), { id: feature.id, latitude: coordinates?.[1], longitude: coordinates?.[0], name, status, trailCount });
  }
  return Array.from(trailheads.values());
}

/**
 * The trailheads the map should draw as full pill pins. The tiles have no ranking of their own, so
 * the busiest trailheads win the limited number of expanded pins and everything else collapses to a
 * dot, which keeps pin density in line with the trail pins on explore.
 */
export function expandedTrailheadIds(trailheads: TrailheadInView[], maxExpandedPins: number): TrailheadInView['id'][] {
  return [...trailheads]
    .sort((a, b) => b.trailCount - a.trailCount || String(a.id).localeCompare(String(b.id)))
    .slice(0, maxExpandedPins)
    .map(({ id }) => id);
}

export function visibleMapQueryBox(
  canvas: { clientWidth: number; clientHeight: number },
  padding?: PaddingOptions
): [[number, number], [number, number]] | undefined {
  if (padding === undefined) {
    return undefined;
  }

  const { top = 0, right = 0, bottom = 0, left = 0 } = padding;
  const maxX = canvas.clientWidth - right;
  const maxY = canvas.clientHeight - bottom;
  if (left >= maxX || top >= maxY) {
    return undefined;
  }

  return [
    [left, top],
    [maxX, maxY]
  ];
}
